import {flow, makeAutoObservable, reaction, runInAction} from 'mobx';
import {executeKick} from '@/shared/api/api';
import {GameState, IGame, PuckType, SpriteConfig, SpriteConfigBasic, SpriteConfigName} from '../types';
import {PreloadedImages} from '@/shared/utils/preload';
import {
    AREA,
    EVENT_SPRITE_DEPENDENCIES,
    GAME_LAYOUT,
    GAME_SPEED,
    GameChangeEvent,
    SPRITE_REGISTRY,
    SPRITES_GATE,
    SPRITES_GOALKEEPER,
    SPRITES_player,
    SPRITES_PUCK
} from "@/entities/game/model/gameLayout";
import {getRandomNumber} from "@/entities/game/model/gameHelpers";
import {RootStore} from "@/app/providers/StoreProvider";
import {InventoryItemsName} from "@/entities/inventory/types";


export class GameStore {

    constructor(private rootStore:RootStore) {
        makeAutoObservable(this);

        reaction(()=> this.userStore.gameInfo, () => {
            this.hitStreak = this.userStore.gameInfo.hitStreak;
            this.createSprites();
        })

        reaction(
            () => this.gameState,
            (state) => {
                if (state === GameState.kickEnd) {
                    runInAction(() => {
                        this.puckType = this.nextPuckType;
                    });
                }
                if(state === GameState.active) {
                    this.applyGameChange(GameChangeEvent.PuckTypeChanged);
                }
            }
        );
    }

    private get effectsStore() {
        return this.rootStore.effectsStore;
    }
    private get userStore() {
        return this.rootStore.userStore;
    }
    private get soundStore() {
        return this.rootStore.soundStore;
    }
    private get inventoryStore() {
        return this.rootStore.inventoryStore;
    }
    private get modalStore() {
        return this.rootStore.modalStore;
    }



    gameState:GameState = GameState.init

    //Коэфф для размеров
    scale = 1

    //Отступ рамок поля после скейла
    get offsetSquare() {
        return GAME_LAYOUT.baseOffsetSquare * this.scale;
    }

    sprites = new Map<SpriteConfigName, SpriteConfig>();

    nextPuckType:PuckType = PuckType.BASIC;
    puckType:PuckType = PuckType.BASIC;

    currentHitInstant: boolean | null = null;

    hitStreak = 0;


    changeState(state:GameState) {
        this.gameState = state;
    }

    getSprite(name: SpriteConfigName) {
        return this.sprites.get(name);
    }
    getItemOptions(name:SpriteConfigName) {
        const itemOptions = this.userStore.gameInfo.itemOptions;
        if(!itemOptions) return null
        return itemOptions.find(x => x.name == name);
    }
    getSpeedMultiplierByStreak(name:SpriteConfigName) {
        if(!EVENT_SPRITE_DEPENDENCIES['HitStreakChanged'].includes(name)) return 1;
        if (this.hitStreak == 2) {
            return 0.6667;
        }
        return 1;
    }
    getSpeed(sprite:SpriteConfigBasic, name:SpriteConfigName){
        const itemOptionSpeed = this.getItemOptions(name)?.speed ?? 1;
        const streakMultiplier = this.getSpeedMultiplierByStreak(name);
        const effectsMultiplier = this.effectsStore.getSpeedMultiplier(name);

        return GAME_SPEED.base *
            sprite.speed *
            itemOptionSpeed *
            streakMultiplier *
            effectsMultiplier *
            this.scale
    }

    createBaseSprite(
        name: SpriteConfigName,
        config: Partial<SpriteConfig>
    ): SpriteConfig {
        return {
            name,
            x: 0,
            y: 0,
            baseWidth:0,
            baseHeight:0,
            width: 0,
            height: 0,
            anchor: 0.5,
            speed: 0,
            image: '',
            visible: this.getItemOptions(name)?.visible ?? true,
            ...config,
        };
    }

    //Базовое создание спрайтов используя дефолтные
    createSprites() {
        const spritePlayerBase = SPRITES_player[PuckType.BASIC];
        const spritePuckBase = SPRITES_PUCK[PuckType.BASIC];
        const spriteGateBase = SPRITES_GATE[PuckType.BASIC];
        const spriteGoalkeeperBase = SPRITES_GOALKEEPER[PuckType.BASIC];

        this.sprites.set(SpriteConfigName.square,
            this.createBaseSprite(SpriteConfigName.square, {
                anchor: 0,
                image: PreloadedImages['square'].src,
                baseWidth: AREA.baseWidth,
                baseHeight: AREA.baseHeight,
            })
        );

        this.sprites.set(SpriteConfigName.player,
            this.createBaseSprite(SpriteConfigName.player, {
                speed: this.getSpeed(spritePlayerBase, SpriteConfigName.player),
                image: PreloadedImages[spritePlayerBase.imageKey].src,
                baseWidth: spritePlayerBase.baseWidth,
                baseHeight: spritePlayerBase.baseHeight,
            })
        );

        this.sprites.set(SpriteConfigName.goalkeeper,
            this.createBaseSprite(SpriteConfigName.goalkeeper, {
                speed: this.getSpeed(spriteGoalkeeperBase, SpriteConfigName.goalkeeper),
                image: PreloadedImages['goalkeeper'].src,
                baseWidth: spriteGoalkeeperBase.baseWidth,
                baseHeight: spriteGoalkeeperBase.baseHeight,
            })
        );

        this.sprites.set(SpriteConfigName.puck,
            this.createBaseSprite(SpriteConfigName.puck, {
                speed: 1,
                image: PreloadedImages[spritePuckBase.imageKey].src,
                baseWidth: spritePuckBase.baseWidth,
                baseHeight: spritePuckBase.baseHeight,
            })
        );

        this.sprites.set(SpriteConfigName.gate,
            this.createBaseSprite(SpriteConfigName.gate, {
                anchor: 0,
                speed: this.getSpeed(spriteGateBase, SpriteConfigName.gate),
                image: PreloadedImages['gate'].src,
                baseWidth: spriteGateBase.baseWidth,
                baseHeight: spriteGateBase.baseHeight,
            })
        );

    }

    setSizeSprite = (sprite:SpriteConfig) =>{
        const baseWidth = sprite.baseWidth;
        const baseHeight = sprite.baseHeight;
        sprite.width = baseWidth * this.scale;
        sprite.height = baseHeight * this.scale;
    }

    initGame = (size: { width: number; height: number; }) =>{
        const screenWidth = size.width;

        const spritePlayer = this.getSprite(SpriteConfigName.player);
        const spriteGoalkeeper = this.getSprite(SpriteConfigName.goalkeeper);
        const spritePuck = this.getSprite(SpriteConfigName.puck);
        const spriteGate = this.getSprite(SpriteConfigName.gate);
        const spriteSquare = this.getSprite(SpriteConfigName.square);



        if (!spritePlayer || !spritePuck || !spriteGate || !spriteSquare || !spriteGoalkeeper ) {
            console.warn("GameStore.initGame: One or more required sprite configs are missing", {
                spritePlayer, spritePuck, spriteGate, spriteSquare, spriteGoalkeeper
            });
            return;
        }
        //Поле
        this.setSizeSprite(spriteSquare);
        spriteSquare.x = AREA.x;
        spriteSquare.y = AREA.y;

        //Ворота
        this.setSizeSprite(spriteGate);
        spriteGate.x = screenWidth/2 - spriteGate.width/2;
        spriteGate.y = this.offsetSquare/2 + (GAME_LAYOUT.topPaddingGate * this.scale) + GAME_LAYOUT.hudH;

        //Хоккеист
        this.setSizeSprite(spritePlayer);
        spritePlayer.x = getRandomNumber(this.offsetSquare, spriteSquare, spritePlayer, spriteGate );
        spritePlayer.y = (spriteSquare.height + GAME_LAYOUT.hudH) - (spritePlayer.height/2) - this.offsetSquare/2 - (GAME_LAYOUT.bottomPaddingPlayer * this.scale);

        //Шайба
        this.setSizeSprite(spritePuck);
        spritePuck.x = spritePlayer.x + spritePlayer.width - (GAME_LAYOUT.paddingPuckForPlayerX * this.scale);
        spritePuck.y = spritePlayer.y - (GAME_LAYOUT.paddingPuckForPlayerY * this.scale);

        //Вратарь
        this.setSizeSprite(spriteGoalkeeper);
        spriteGoalkeeper.x = screenWidth/2;
        spriteGoalkeeper.y = spriteGate.y + (GAME_LAYOUT.paddingGoalkeeper * this.scale);

        this.updateSettingsSprite(SpriteConfigName.goalkeeper)

    }

    updatePositionSprite = (spriteName:SpriteConfigName, x?:number, y?:number) =>{
        const spriteStore = this.getSprite(spriteName);
        if(!spriteStore)return;
        if(!x || !y) return;
        spriteStore.x = x;
        spriteStore.y = y;
    }

    updateSettingsSprite = (spriteName:SpriteConfigName)=> {
        const spriteType = SPRITE_REGISTRY[spriteName][this.puckType];
        const sprite = this.getSprite(spriteName);
        if(sprite){
            sprite.image = PreloadedImages[spriteType.imageKey].src;
            sprite.baseWidth = spriteType.baseWidth;
            sprite.baseHeight = spriteType.baseHeight;
            sprite.visible = this.effectsStore.getVisibleMultiplier(spriteName);
            this.setSizeSprite(sprite);
            if(sprite.name == SpriteConfigName.puck){
                sprite.speed = spriteType.speed
            }else{
                sprite.speed = this.getSpeed(spriteType, sprite.name);
            }
        }
    }


    applyGameChange = (event: GameChangeEvent) => {

        const targets = EVENT_SPRITE_DEPENDENCIES[event];
        if (!targets) return;

        targets.forEach(spriteName => {
            this.updateSettingsSprite(spriteName);
        });

    };


    setGameStart() {
        if(this.gameState == GameState.init && this.userStore.gameInfo.user.energy !=0){
            this.gameState = GameState.active;
            this.soundStore.play('noise');
        }
    }
    setGameStop(){
        this.gameState = GameState.init;
        this.soundStore.setEnabled(false);
    }
    setGameAwait(){
        this.gameState = GameState.await;
    }
    setGameContinue(){
        if(this.gameState != GameState.pause){
        }else{
            if (this.soundStore.enabled) this.soundStore.play('noise');
        }

        if(this.userStore.gameInfo.user.energy !=0){
            this.gameState = GameState.active;
        }else{
            this.setGameStop();
        }
    }
    setGamePause(){
        if(this.gameState == GameState.active){
            this.gameState = GameState.pause;
            this.soundStore.stopAll();
        }
    }
    onKickClick = () => {
        if(this.gameState == GameState.active){
            this.gameState = GameState.kick
        }
    }
    setResult(value: boolean) {
        this.soundStore.play(value?'goal':'miss')
    }

    //Используем для индикатора попадания
    setInstantHit(val: boolean | null) {
        this.currentHitInstant = val;
    }

    addRawScore(value: number) {
        this.userStore.gameInfo.user.score += value;
        this.userStore.user.score += value;
    }
    addRawEnergy(value: number) {
        this.userStore.gameInfo.user.energy += value;
    }

    setGameKick = flow(function* (this: GameStore, hit: boolean) {
        this.setResult(hit);
        this.userStore.gameInfo.user.energy -= 1;
        if(hit){
            const multiScore = this.effectsStore.getScoreMultiplier();
            this.addRawScore((this.puckType === PuckType.FIRE ? 5 : 1) * multiScore);

            if(this.puckType === PuckType.GOLDEN) this.userStore.gameInfo.user.goldenScore += 1;

            this.hitStreak += 1;
        }else{
            this.hitStreak = 0;
        }


        this.applyGameChange(GameChangeEvent.HitStreakChanged);

        const effectsChanged = this.inventoryStore.onHit();
        this.inventoryStore.onHitStreakChanged(this.hitStreak);
        this.applyGameChange(GameChangeEvent.InventoryBonusChanged);

        if (effectsChanged[InventoryItemsName.OviFlex100]) {
            this.modalStore.open("destroyOviFlex",{});
        }

        const gameData: IGame = { hit, type: this.puckType };

        const kick = yield executeKick(gameData);
        if (kick.success) {
            this.nextPuckType = kick.data.nextPuckType;
        }
    });
}
